import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Container,
  CircularProgress,
  useTheme
} from '@mui/material';
import { Business, Assignment } from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import { httpRequest } from 'src/services/httpRequest';

// TypeScript interfaces
interface DocumentData {
  DocumentName: string;
  downloadCount: string;
}

interface UserData {
  FirstName: string;
  LastName: string;
  EmailID: string;
  Last_Login: string;
}

interface TopAwardedVendor {
  Business: string;
  AwardedBids: string;
}

interface DashboardData {
  TotalVendors: number;
  ActiveVendors: number;
  PendingVendors: number;
  TotalCategories: number;
  TotalBids: number;
  AwardedBids: number;
  OpenBids: number;
  CloseBids: number;
  DraftBids: number;
  TopAwardedVendors: TopAwardedVendor[];
  MostDownloadedDocuments: DocumentData[];
  TopLoggedInUsers: UserData[];
}

interface MetricCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color?: string;
}

interface StatusCardProps {
  title: string;
  value: number;
  color: string;
}

// Default/fallback data structure
const defaultDashboardData: DashboardData = {
  TotalVendors: 0,
  ActiveVendors: 0,
  PendingVendors: 0,
  TotalCategories: 0,
  TotalBids: 0,
  AwardedBids: 0,
  OpenBids: 0,
  CloseBids: 0,
  DraftBids: 0,
  TopAwardedVendors: [],
  MostDownloadedDocuments: [],
  TopLoggedInUsers: []
};

const Dashboard: React.FC = () => {
  const [dashboardData, setDashboardData] =
    useState<DashboardData>(defaultDashboardData);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();

  // Fetch dashboard data when component loads
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const result = await httpRequest('GET', '/auth/dashboard_counts');

        if (result?.status === 'success' && result.data) {
          setDashboardData(result.data);
          setError(null);
        } else {
          setError('Failed to load dashboard data');
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Error loading dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Prepare pie chart data
  const pieData = [
    { name: 'Awarded', value: dashboardData.AwardedBids, color: '#26C6DA' },
    { name: 'Closed', value: dashboardData.CloseBids, color: '#EF5350' },
    // { name: 'Draft', value: dashboardData.DraftBids, color: '#FFA726' },
    { name: 'Open', value: dashboardData.OpenBids, color: '#66BB6A' }
  ];

  // Prepare bar chart data for document downloads
  const barData = dashboardData.MostDownloadedDocuments.map((doc, index) => ({
    name:
      doc.DocumentName.length > 15
        ? `${doc.DocumentName.substring(0, 15)}...`
        : doc.DocumentName,
    downloads: parseInt(doc.downloadCount),
    color: [
      '#26C6DA',
      '#66BB6A',
      '#9C27B0',
      '#FF9800',
      '#2196F3',
      '#FFEB3B',
      '#F44336'
    ][index % 7]
  }));

  // Get top 5 awarded vendors from API data
  const getTopAwardedVendors = () => {
    return dashboardData.TopAwardedVendors.slice(0, 5).map((vendor) => ({
      name: vendor.Business,
      awards: parseInt(vendor.AwardedBids)
    }));
  };

  const MetricCard: React.FC<MetricCardProps> = ({
    title,
    value,
    icon,
    color = '#1976d2'
  }) => (
    <Card
      sx={{
        height: 140,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          transform: 'translateY(-2px)',
          transition: 'all 0.3s ease-in-out'
        }
      }}
    >
      <CardContent
        sx={{
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 2
        }}
      >
        <Box sx={{ color: color, mb: 1.5 }}>{icon}</Box>
        <Typography
          variant="h4"
          component="div"
          fontWeight="bold"
          sx={{ mb: 0.5, color: '#1a1a1a' }}
        >
          {value}
        </Typography>
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{
            fontSize: '0.85rem',
            lineHeight: 1.2,
            textAlign: 'center',
            height: '2.4em',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  const StatusCard: React.FC<StatusCardProps> = ({ title, value, color }) => (
    <Card
      sx={{
        height: 140,
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          transform: 'translateY(-2px)',
          transition: 'all 0.3s ease-in-out'
        }
      }}
    >
      <CardContent
        sx={{
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 2
        }}
      >
        <Chip
          label={title}
          sx={{
            backgroundColor: color,
            color: 'white',
            mb: 1.5,
            minWidth: 80,
            fontWeight: 'bold',
            fontSize: '0.85rem'
          }}
        />
        <Typography
          variant="h4"
          component="div"
          fontWeight="bold"
          sx={{ color: '#1a1a1a' }}
        >
          {value}
        </Typography>
      </CardContent>
    </Card>
  );

  // Show loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '90vh',
          p: 5
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <Typography color="error">{error}</Typography>
        </Box>
      </Container>
    );
  }

  const topAwardedVendors = getTopAwardedVendors();

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 85,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          Dashboard
        </Typography>
      </Box>
      <Grid container spacing={3}>
        {/* Top Metrics Row with uniform height */}
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="Registered Vendors"
            value={dashboardData.TotalVendors}
            icon={<Business fontSize="large" />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="Total Bids"
            value={dashboardData.TotalBids}
            icon={<Assignment fontSize="large" />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatusCard
            title="Awarded"
            value={dashboardData.AwardedBids}
            color="#26C6DA"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatusCard
            title="Open"
            value={dashboardData.OpenBids}
            color="#66BB6A"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatusCard
            title="Closed"
            value={dashboardData.CloseBids}
            color="#EF5350"
          />
        </Grid>
        {/* <Grid item xs={12} sm={6} md={2}>
          <StatusCard
            title="Draft"
            value={dashboardData.DraftBids}
            color="#FFA726"
          />
        </Grid> */}

        {/* Main Content Row */}
        <Grid item xs={12} lg={12}>
          <Grid container spacing={3}>
            {/* Bid Details Pie Chart */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '400px',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    Bid Details
                  </Typography>
                  <Box
                    sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}
                  >
                    <Typography variant="body2">Status:</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            backgroundColor: '#26C6DA',
                            borderRadius: '50%'
                          }}
                        />
                        <Typography variant="caption">Awarded</Typography>
                      </Box>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            backgroundColor: '#EF5350',
                            borderRadius: '50%'
                          }}
                        />
                        <Typography variant="caption">Closed</Typography>
                      </Box>
                      {/* <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            backgroundColor: '#FFA726',
                            borderRadius: '50%'
                          }}
                        />
                        <Typography variant="caption">Draft</Typography>
                      </Box> */}
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            backgroundColor: '#66BB6A',
                            borderRadius: '50%'
                          }}
                        />
                        <Typography variant="caption">Open</Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      height: 300,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& .recharts-wrapper, & .recharts-surface, & svg, & path, & g': {
                        outline: 'none'
                      }
                    }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Most Downloaded Documents Bar Chart */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '400px',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    Most Downloaded Documents
                  </Typography>
                  <Box sx={{ height: 320 }}>
                    {dashboardData.MostDownloadedDocuments.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={barData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                        >
                          <XAxis
                            dataKey="name"
                            tick={{ fontSize: 10 }}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                          />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="downloads">
                            {barData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%'
                        }}
                      >
                        <Typography color="text.secondary">
                          No documents downloaded yet
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* New Row for Top 5 Bid Awarded Users and Top Logged-in Users */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              height: '400px',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <CardContent
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                p: 3
              }}
            >
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Top 5 Bid Awarded Users
              </Typography>
              <TableContainer sx={{ flexGrow: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#1976d2' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                        Awarded To
                      </TableCell>
                      <TableCell
                        sx={{ color: 'white', fontWeight: 'bold' }}
                        align="center"
                      >
                        No of Awards
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topAwardedVendors.length > 0 ? (
                      <>
                        {topAwardedVendors.map((vendor, index) => (
                          <TableRow key={index}>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {vendor.name}
                            </TableCell>
                            <TableCell align="center">
                              {vendor.awards}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ backgroundColor: '#666' }}>
                          <TableCell
                            sx={{ color: 'white', fontWeight: 'bold' }}
                          >
                            Total
                          </TableCell>
                          <TableCell
                            sx={{ color: 'white', fontWeight: 'bold' }}
                            align="center"
                          >
                            {topAwardedVendors.reduce(
                              (sum, vendor) => sum + vendor.awards,
                              0
                            )}
                          </TableCell>
                        </TableRow>
                      </>
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{ textAlign: 'center', py: 4 }}
                        >
                          <Typography color="text.secondary">
                            No awarded bids yet
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              height: '400px',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <CardContent
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                p: 3
              }}
            >
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Top Logged-in Users
              </Typography>
              <TableContainer sx={{ flexGrow: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#1976d2' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                        Name
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                        Email
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData.TopLoggedInUsers.length > 0 ? (
                      dashboardData.TopLoggedInUsers.slice(0, 5).map(
                        (user, index) => (
                          <TableRow key={index}>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {`${user.FirstName} ${user.LastName}`}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {user.EmailID}
                            </TableCell>
                          </TableRow>
                        )
                      )
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{ textAlign: 'center', py: 4 }}
                        >
                          <Typography color="text.secondary">
                            No users logged in recently
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
